import ManageHistoryOutlinedIcon from '@mui/icons-material/ManageHistoryOutlined';
import SettingsOutlinedIcon from '@mui/icons-material/SettingsOutlined';
import {
	Box,
	Paper,
	Table,
	TableBody,
	TableContainer,
	TableHead,
	TableRow,
	TextField,
	Tooltip,
	Typography
} from '@mui/material';
import AuthContext from 'context/AuthContext';
import i18n from 'i18n';
import React, { useCallback, useContext, useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { USER_ROLES } from 'utils/constants';
import { ellipsis } from 'utils/utils';
import ButtonIcon from './ButtonIcon';
import CustomTableCell from './CustomTableCell';
import GradeBadge from './GradeBadge';
import { StyledTableSortLabel } from './TableSortedLabel';
interface Column {
	id: string;
	visible: boolean;
	title: string;
	order: number;
	isEditable?: boolean; // Ajout de la propriété isEditable
}

interface LayersTableProps {
	tempschema: any[];
	openSettings: boolean;
	openResetDialog: boolean;
	columns: Column[];
	brushTempsData: string[];
	colorSet: { key: string; color: string }[];
	onRowHover: (layerId: number | null) => void;
	handleOpenSettings: () => void; // Nouvelle prop pour gérer l'ouverture des paramètres
	handleOpenResetDialog: () => void; // Nouvelle prop pour gérer l'ouverture des paramètres
	handleResetBrush: () => void; // Nouvelle prop pour gérer l'ouverture des paramètres
	onUpdateTempschema?: (updatedTempschema: any[]) => void; // Nouvelle prop pour mettre à jour tempschema
}

type Order = 'asc' | 'desc';

const DimensionTable: React.FC<LayersTableProps> = ({ tempschema, brushTempsData, openSettings, columns, colorSet, onRowHover, handleOpenSettings, handleOpenResetDialog, openResetDialog, onUpdateTempschema, handleResetBrush }) => {

	const { t } = useTranslation()
	const [selectedLayers, setSelectedLayers] = useState<number[]>([]);
	const [orderBy, setOrderBy] = useState<string>('idx');
	const [order, setOrder] = useState<Order>('desc');
	const [sortedTempschema, setSortedTempschema] = useState(tempschema);
	const [editingCell, setEditingCell] = useState<{ layerId: number, columnId: string } | null>(null);
	const { role } = useContext(AuthContext)
	// Match Chart RTL axis order (V4 shell forces direction:ltr, so CSS won't mirror the table).
	const isRtl = (i18n.language || '').startsWith('he');
	useEffect(() => {
		if (brushTempsData.length > 0) {
			const updatedSortedTempschema = tempschema
				.filter((layer) => brushTempsData.includes(layer.id))
				.sort((a, b) => b.idx - a.idx);
			setSortedTempschema(updatedSortedTempschema);
			setOrderBy('idx');
			setOrder('desc');
		} else {
			// Default table order: idx descending (highest # first).
			const initialSorted = [...tempschema].sort((a, b) => b.idx - a.idx);
			setSortedTempschema(initialSorted);
			setOrderBy('idx');
			setOrder('desc');
		}
	}, [brushTempsData, tempschema]);

	const handleClick = (layerId: number) => {
		const selectedIndex = selectedLayers.indexOf(layerId);
		let newSelected: number[] = [];

		if (selectedIndex === -1) {
			newSelected = newSelected.concat(selectedLayers, layerId);
		} else if (selectedIndex === 0) {
			newSelected = newSelected.concat(selectedLayers.slice(1));
		} else if (selectedIndex === selectedLayers.length - 1) {
			newSelected = newSelected.concat(selectedLayers.slice(0, -1));
		} else if (selectedIndex > 0) {
			newSelected = newSelected.concat(
				selectedLayers.slice(0, selectedIndex),
				selectedLayers.slice(selectedIndex + 1),
			);
		}

		setSelectedLayers(newSelected);
	};


	const handleSort = (property: string) => {
		const nextOrder: Order =
			orderBy === property && order === 'asc' ? 'desc' : 'asc';
		setOrder(nextOrder);
		setOrderBy(property);

		const sorted = [...sortedTempschema].sort((a, b) => {
			const aValue = a[property];
			const bValue = b[property];
			if (typeof aValue === 'number' && typeof bValue === 'number') {
				return nextOrder === 'asc' ? aValue - bValue : bValue - aValue;
			}
			const aStr = aValue != null ? String(aValue) : '';
			const bStr = bValue != null ? String(bValue) : '';
			return nextOrder === 'asc'
				? aStr.localeCompare(bStr)
				: bStr.localeCompare(aStr);
		});
		setSortedTempschema(sorted);
	};

	const handleEditText = (layerId: number, columnId: string, value: string) => {
		// Mettre à jour tempschema sans déclencher de tri
		const updatedTempschema = tempschema.map(layer =>
			layer.id === layerId ? { ...layer, [columnId]: value } : layer
		);

		// Mettre à jour sortedTempschema en préservant l'ordre actuel
		const updatedSortedTempschema = sortedTempschema.map(layer =>
			layer.id === layerId ? { ...layer, [columnId]: value } : layer
		);

		setSortedTempschema(updatedSortedTempschema);
		onUpdateTempschema?.(updatedTempschema);
	};

	const visibleColumns = useMemo(() => {
		const cols = columns.filter((column) => column.visible);
		// Same order as Chart orderedDimensions when Hebrew: Rating/Grade … # (left→right).
		return isRtl ? [...cols].reverse() : cols;
	}, [columns, isRtl]);

	const getRatingLetter = (rating: number) => {
		return colorSet[rating]?.key || '';
	};

	const handleRowMouseEnter = useCallback((layer: any) => {
		onRowHover(layer);
	}, [onRowHover]);

	const handleRowMouseLeave = useCallback(() => {
		onRowHover(null);
	}, [onRowHover]);

	return (
		<>
			{role !== USER_ROLES.VIEWER && (
				<Box display="flex" justifyContent="space-between" alignItems="center" >
					<Box style={{ display: "flex" }}>
						<Tooltip title={t('SETTINGS_LAYOUT')}>
							<ButtonIcon onClick={handleOpenSettings} isActive={openSettings}  >
								{/* 	<SettingsRoundedIcon /> */}
								<SettingsOutlinedIcon />
							</ButtonIcon>
						</Tooltip>
						{/* <Typography variant="h6" component="h2">
						Tableau des dimensions
					</Typography> */}
					</Box>
					<div>
						<Tooltip title={t('RESET_LAYOUT')} >
							<ButtonIcon isActive={openResetDialog} onClick={handleOpenResetDialog} >
								<ManageHistoryOutlinedIcon />
							</ButtonIcon>
						</Tooltip>
						{/* 	<Tooltip title={t('RESET_BRUSH')}>
						<IconButton onClick={handleResetBrush} >
							<RefreshIcon />
						</IconButton>
					</Tooltip> */}
					</div>
				</Box>
			)}
			<TableContainer
				component={Paper}
				sx={{
					'& .MuiTableCell-root': {
						p: 0
					},
					//maxHeight: '400px', // hauteur maximale fixe
					overflow: 'auto',   // active le défilement
					flex: '1 1 auto',
					//minHeight: '200px'  // hauteur minimale
				}}
			>
				<Table size="small" >
					<TableHead>
						<TableRow sx={{ '&:hover': { boxShadow: 'none' } }}>
							{visibleColumns.map((column) => (
								<CustomTableCell key={column.id}
									sx={{ textAlign: "center" }}
									width={column.id === "idx" ? "40px" : column.id === 'rating' || column.id === 'weighed_grade' ? '40px' : '50px'}>
									{column.id == 'weighed_grade' ? (
										<StyledTableSortLabel
											active={orderBy === column.id}
											direction={orderBy === column.id ? order : 'asc'}
											onClick={() => handleSort(column.id)}
										>
											<Typography sx={{ cursor: "default" }} dir={"ltr"} variant='subtitle1' fontWeight="bold" title={column.title}>
												{ellipsis(column.title, 7)}
											</Typography>
										</StyledTableSortLabel>
									) : column.id == 'idx' ? (
										<StyledTableSortLabel
											active={orderBy === column.id}
											direction={orderBy === column.id ? order : 'asc'}
											onClick={() => handleSort(column.id)}
										>
											<Typography dir={"ltr"} variant='subtitle1' fontWeight="bold" title={column.title}>
												{ellipsis(column.title, 7)}
											</Typography>
										</StyledTableSortLabel>
									) : (
										<Typography dir={"ltr"} variant='subtitle1' fontWeight="bold" title={column.title}>
											{ellipsis(column.title, 7)}
										</Typography>
									)}

								</CustomTableCell>
							))}
						</TableRow>
					</TableHead>
					<TableBody>
						{sortedTempschema
							.map((layer) => (
								<TableRow

									key={layer.id}
									onClick={() => handleClick(layer.id)}
									onMouseEnter={() => handleRowMouseEnter(layer)}
									onMouseLeave={handleRowMouseLeave}
								>
									{visibleColumns.map((column) => (
										<CustomTableCell
											clickable={false}
											key={column.id}
											style={{ textAlign: "center" }}
										>
											{column.isEditable && editingCell?.layerId === layer.id && editingCell?.columnId === column.id ? (
												<TextField
													value={layer[column.id] || ''}
													onChange={(e) => handleEditText(layer.id, column.id, e.target.value)}
													onBlur={() => setEditingCell(null)}
													autoFocus
													variant="outlined"
													size="small"
													sx={{
														width: "50px",
														'& .MuiInputBase-input': {
															padding: '4px 8px',
															fontSize: '14px',
														}
													}}
												/>
											) : column.isEditable ? (
												<Typography
													variant='caption'
													noWrap
													onMouseEnter={() => setEditingCell({ layerId: layer.id, columnId: column.id })}
													onMouseLeave={() => setEditingCell(null)}
													sx={{ cursor: 'pointer' }}
												>
													{layer[column.id]}
												</Typography>
											) : column.id === 'name' ? (
												<Typography variant='caption' noWrap >
													{layer[column.id]}
												</Typography>
											) : column.id === 'rating' ? (
												<Typography variant='caption' noWrap dir={"ltr"} sx={{ display: "flex", justifyContent: "center" }} >
													<GradeBadge rating={getRatingLetter(layer[column.id])} weighed_grade={layer['weighed_grade']} />
												</Typography>
											) : column.id === 'weighed_grade' ? (
												<Typography variant='caption' dir={"ltr"} noWrap>
													{layer[column.id]}
												</Typography>
											) : (
												<Typography variant='caption' noWrap >
													{layer[column.id]}
												</Typography>
											)}
										</CustomTableCell>
									))}
								</TableRow>
							))}
					</TableBody>
				</Table>
			</TableContainer>
		</>
	);
};

export default DimensionTable;